/**
 * `useGridKeyboardNav` — the WAI-ARIA grid keyboard + focus engine TanStack does
 * not ship. Two non-obvious contracts:
 *  - Focus via `aria-activedescendant` on the never-unmounting grid container, NOT
 *    roving `tabIndex`: virtualization destroys the focused cell on scroll-away,
 *    dropping focus to `<body>`. The active cell carries `data-active`, not
 *    `:focus` — real focus never leaves the container, so a `:focus` ring never paints.
 *  - The active position is a clamped `{ rowIndex, colIndex }` COORDINATE in
 *    logical-row / visible-column space, never a row id: a server sort preserves
 *    the coordinate (focus-after-sort), a filter clamps it to the last valid cell.
 * Engine-agnostic by taking plain inputs (counts, a `getCellId` factory, scroll /
 * mounted-range callbacks), so it unit-tests in jsdom with no TanStack.
 */
import { type KeyboardEvent as ReactKeyboardEvent, useMemo, useRef, useState } from 'react'
import { useEvent } from 'react-use-event-hook'

/**
 * Pure cell-id builder — the single source of the id formula the nav hook and the
 * memoized body rows share (`gridId` + `activeCellId` travel as primitive props; a
 * per-render `getCellId` closure would defeat the rows' memo bailout).
 */
export const buildGridCellId = (gridId: string, { rowIndex, colIndex }: GridCoord): string =>
  `${gridId}-cell-${rowIndex}-${colIndex}`

/** A cell position in logical-row × visible-column space. Header is not addressable. */
export type GridCoord = {
  /** 0-based logical row index, NOT the DOM/virtual index. */
  rowIndex: number
  colIndex: number
}

/**
 * The mounted (windowed) logical row range `[start, end)`. Used to re-anchor after
 * a scroll-away: when the active row is outside it the DOM node is gone, so the
 * next key press starts from the first visible row, not a stale coordinate.
 */
export type MountedRange = { start: number; end: number }

export type UseGridKeyboardNavOptions = {
  /** Loaded (logical, deduped) row count. */
  rowCount: number
  /** Visible column count (`table.getVisibleLeafColumns().length`). */
  colCount: number
  /**
   * Build the DOM `id` for a cell; MUST match the `id` on the rendered `gridcell`
   * so `aria-activedescendant` resolves. Defaults to `${gridId}-cell-${row}-${col}`.
   */
  getCellId: (coord: GridCoord) => string
  /**
   * Bring a logical row into the virtualizer window (keep-active-row-mounted) so
   * the referenced cell `id` resolves to a live node before the next paint.
   */
  scrollToLogicalIndex: (logicalIndex: number) => void
  /** Current mounted logical row range, read at key-press time to detect scroll-away. */
  getMountedLogicalRange: () => MountedRange
  /** Page size for PageUp/PageDown. Defaults to 10. */
  pageSize?: number
  /** Fired when Space toggles selection on the active cell. */
  onToggleSelect?: (coord: GridCoord) => void
  /** Fired when Enter fires the row action. */
  onActivate?: (coord: GridCoord) => void
}

export type GridKeyboardNav = {
  activeCoord: GridCoord | null
  /** `aria-activedescendant` for the grid container: the active cell's id, or `undefined`. */
  activeDescendantId: string | undefined
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void
  onFocus: () => void
  isActive: (coord: GridCoord) => boolean
  /** Imperatively set the active cell (e.g. on pointer click into a cell). Clamped. */
  setActiveCoord: (coord: GridCoord) => void
}

const DEFAULT_PAGE_SIZE = 10

/**
 * Clamp a coordinate into `[0, rowCount) × [0, colCount)` — the single guard that
 * makes focus-after-sort / focus-clamp-after-filter correct: a coordinate can never
 * address a row/col that no longer exists. Empty grid → `null`.
 */
export const clampCoord = (
  coord: GridCoord,
  rowCount: number,
  colCount: number,
): GridCoord | null => {
  if (rowCount <= 0 || colCount <= 0) {
    return null
  }

  const rowIndex = Math.min(Math.max(coord.rowIndex, 0), rowCount - 1)
  const colIndex = Math.min(Math.max(coord.colIndex, 0), colCount - 1)

  return { rowIndex, colIndex }
}

const isRowMounted = (rowIndex: number, range: MountedRange): boolean =>
  rowIndex >= range.start && rowIndex < range.end

/** Keys the grid owns; everything else bubbles (native filter/link behavior kept). */
const GRID_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Enter',
  ' ',
  'Spacebar', // legacy Edge/IE Space value
])

export const useGridKeyboardNav = (options: UseGridKeyboardNavOptions): GridKeyboardNav => {
  const {
    rowCount,
    colCount,
    getCellId,
    scrollToLogicalIndex,
    getMountedLogicalRange,
    pageSize = DEFAULT_PAGE_SIZE,
    onToggleSelect,
    onActivate,
  } = options

  const [rawActiveCoord, setRawActiveCoord] = useState<GridCoord | null>(null)

  // Re-clamp the rendered active coordinate every render against live counts:
  // focus-after-sort / filter-shrink made declarative, so a stale index can never
  // reach `aria-activedescendant` or `isActive`. Derived, not stored, so it can't drift.
  const clampedActive = useMemo<GridCoord | null>(() => {
    if (rawActiveCoord === null) {
      return null
    }

    return clampCoord(rawActiveCoord, rowCount, colCount)
  }, [rawActiveCoord, rowCount, colCount])

  // Mirror the CLAMPED coordinate (not the raw one) so event-time handlers resume
  // from the same cell the render announces via `aria-activedescendant`; mirroring
  // raw would let a post-sort/filter key press act on a row that no longer exists
  // (wrong-row interaction).
  const coordRef = useRef<GridCoord | null>(null)
  coordRef.current = clampedActive

  const commit = useEvent((next: GridCoord | null): void => {
    if (next === null) {
      setRawActiveCoord(null)

      return
    }
    const clamped = clampCoord(next, rowCount, colCount)
    setRawActiveCoord(clamped)
    if (clamped !== null) {
      // keep-active-row-mounted: resolve the referenced `id` to a live node before paint.
      scrollToLogicalIndex(clamped.rowIndex)
    }
  })

  // The coordinate a key press navigates FROM. If the active row has windowed out
  // (wheel scroll unmounted it), re-anchor to the first visible row rather than
  // navigate from a dead coordinate.
  const resolveOrigin = useEvent((): GridCoord => {
    const current = coordRef.current
    if (current === null) {
      return { rowIndex: 0, colIndex: 0 }
    }

    const range = getMountedLogicalRange()
    if (isRowMounted(current.rowIndex, range)) {
      return current
    }

    return { rowIndex: Math.max(range.start, 0), colIndex: current.colIndex }
  })

  const onFocus = useEvent((): void => {
    if (coordRef.current === null) {
      commit({ rowIndex: 0, colIndex: 0 })
    }
  })

  const onKeyDown = useEvent((event: ReactKeyboardEvent<HTMLElement>): void => {
    if (!GRID_KEYS.has(event.key)) {
      return
    }

    const origin = resolveOrigin()

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault()
        commit({ rowIndex: origin.rowIndex - 1, colIndex: origin.colIndex })

        return
      case 'ArrowDown':
        event.preventDefault()
        commit({ rowIndex: origin.rowIndex + 1, colIndex: origin.colIndex })

        return
      case 'ArrowLeft':
        event.preventDefault()
        commit({ rowIndex: origin.rowIndex, colIndex: origin.colIndex - 1 })

        return
      case 'ArrowRight':
        event.preventDefault()
        commit({ rowIndex: origin.rowIndex, colIndex: origin.colIndex + 1 })

        return
      case 'Home':
        event.preventDefault()
        // Ctrl+Home → first cell of the grid; Home → first cell of the row.
        commit(
          event.ctrlKey || event.metaKey
            ? { rowIndex: 0, colIndex: 0 }
            : { rowIndex: origin.rowIndex, colIndex: 0 },
        )

        return
      case 'End':
        event.preventDefault()
        // Ctrl+End → last cell of the grid; End → last cell of the row.
        commit(
          event.ctrlKey || event.metaKey
            ? { rowIndex: rowCount - 1, colIndex: colCount - 1 }
            : { rowIndex: origin.rowIndex, colIndex: colCount - 1 },
        )

        return
      case 'PageUp':
        event.preventDefault()
        commit({ rowIndex: origin.rowIndex - pageSize, colIndex: origin.colIndex })

        return
      case 'PageDown':
        event.preventDefault()
        commit({ rowIndex: origin.rowIndex + pageSize, colIndex: origin.colIndex })

        return
      case ' ':
      case 'Spacebar': {
        // `preventDefault` stops the browser page-scroll on the focused container.
        event.preventDefault()
        const target = clampCoord(origin, rowCount, colCount)
        if (target !== null) {
          onToggleSelect?.(target)
        }

        return
      }
      case 'Enter': {
        event.preventDefault()
        const target = clampCoord(origin, rowCount, colCount)
        if (target !== null) {
          onActivate?.(target)
        }

        return
      }
      default:
        return
    }
  })

  const setActiveCoord = useEvent((coord: GridCoord): void => {
    commit(coord)
  })

  // Called DURING RENDER per cell, so NOT a `useEvent` (that throws pre-mount).
  // Memoized so its identity stays stable across renders with the same active cell;
  // a fresh closure per render would destabilize every cell under the React Compiler.
  const isActive = useMemo<(coord: GridCoord) => boolean>(() => {
    const active = clampedActive

    return (coord: GridCoord): boolean => {
      if (active === null) {
        return false
      }

      return active.rowIndex === coord.rowIndex && active.colIndex === coord.colIndex
    }
  }, [clampedActive])

  const activeDescendantId = clampedActive === null ? undefined : getCellId(clampedActive)

  return {
    activeCoord: clampedActive,
    activeDescendantId,
    onKeyDown,
    onFocus,
    isActive,
    setActiveCoord,
  }
}
